import { Button, IconButton, StyledText } from 'UI';
import SuccessModal from 'UI/Modals/SuccessModal';
import { TermEnum } from 'UI/PriceLabel/PriceLabel';
import { ManualTypeEnum, PaymentDtoLevelEnum, PaymentDtoTypeEnum, SubscribeDtoTypeEnum } from 'api/generated';
import { CaretIcon, UserIcon } from 'assets/icons';
import { StreakModal, UpgradeSubscriptionModal } from 'components';
import { SubscriptionType } from 'components/Sidebar/Sidebar';
import StudyThisButton from 'components/StydyThisButton';
import { SubscribeType, accessLevels } from 'constant';
import { queryConstants, routes } from 'constant/routes';
import {
  useAsyncAction,
  useBundleUpsell,
  useGetAmplitudeExperimentInfo,
  useGoogleAnalytics,
  useNotifications,
  useQuery,
} from 'hooks';
import { Steps } from 'intro.js-react';
import 'intro.js/introjs.css';
import MainLayout from 'layouts/MainLayout';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StorageKeys } from 'services';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import { CourseOptionsForPayment } from 'store/ducks/courses/types';
import styled, { css, useTheme } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { widthBreakpoints } from 'styles/general/variables';
import { areDebugToolsEnabled, canUserAccessTextbook, getCoursePrimaryColor } from 'utils';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

import { NoStreakModal, StreakFireButton } from '../../components';
import { CalendarButton, StudyPlan, SuggestionButton } from './components';
import { StepInfo, TextbookStepInfo } from './components';
import Bonuses from './components/Bonuses';
import InstructionManualModal from './components/InstructionManualModal';
import Performance from './components/Performance';
import PerformancePlug from './components/PerformancePlug';
import Progress from './components/Progress';
import './style.css';

type HomeProps = {
  subscription?: SubscriptionType;
};

/** One onboarding step, before it is turned into an intro.js step object. */
type OnboardingStepDef = {
  key: string;
  title: string;
  description: string;
  element?: string;
  position?: string;
  /** Uses the 600px video-sized tooltip. */
  isWide?: boolean;
  /** Uses the 648px feature-grid tooltip and TextbookStepInfo. */
  isTextbook?: boolean;
  showVideo?: boolean;
  showUpgradeBullets?: boolean;
};

const Home: FC<HomeProps> = () => {
  const theme = useTheme();
  const { colors } = theme;
  const dispatch = useAppDispatch();
  const { errorToast } = useNotifications();
  const { getEventSubscriptionPaymentModalOpenedGA } = useGoogleAnalytics();
  const { getOnboardingVariant } = useGetAmplitudeExperimentInfo();
  const { handleUpgradeBundle } = useBundleUpsell();
  const query = useQuery();

  const navigate = useNavigate();

  const planRef = useRef<HTMLDivElement>(null);

  const [_variant, setVariant] = useState<undefined | string>(undefined);
  // console.log('🚀 ~ variant:', _variant);
  const [isShowUpgradeModal, setIsShowUpgradeModal] = useState(false);
  const [isInstructionShowModal, setIsShowInstructionModal] = useState(false);
  const [isShowInviteModal, setIsShowInviteModal] = useState(false);
  const [isShowStreakModal, setIsShowStreakModal] = useState(false);
  const isDarkMode = useAppSelector(selectors.settings.isDarkMode);
  const isFirstLogin = useAppSelector((store) => store.user.user?.isFirstLogin);
  const activeCourse = useAppSelector(selectors.courses.selectActiveCourse);
  const courses = useAppSelector(selectors.courses.selectCourses);
  const inviteCourse = useAppSelector(selectors.courses.inviteCourse);
  const inviteCourseId = useAppSelector(selectors.settings.inviteCourseId);
  const isLandingPage = useAppSelector(selectors.auth.selectIsLandingPage);
  const fullName = useAppSelector((store) => store.user.user?.name);
  const streakCount = useAppSelector((store) => store.performance.courseStatistic?.streak);
  const hasNoStreak = !Number(streakCount);
  const paymentLevel = useAppSelector(selectors.courses.selectCurrentPaymentLevel);
  const paymentType = useAppSelector(selectors.courses.selectCurrentPaymentType);
  // Bundle upgrade/upsell modal is mounted globally in CourseProvider
  const isGoldPaymentOrTrial = paymentLevel === PaymentDtoLevelEnum.Gold || paymentType === PaymentDtoTypeEnum.Free;

  // Check if user has any purchased bundles
  const userBundles = courses?.filter((course) => (course.type as string) === 'bundle') || [];
  const hasPurchasedBundle = userBundles.some(
    (bundle) => bundle.currentPayment && bundle.currentPayment.type !== PaymentDtoTypeEnum.Free,
  );

  // Don't show upgrade button if user has purchased bundles, even if active course is free
  const isTrial = paymentType === PaymentDtoTypeEnum.Free && !hasPurchasedBundle;

  const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');

  const [stepsEnabled, setStepsEnabled] = useState(false);

  const isDebugMode = areDebugToolsEnabled();
  // Evaluates both domain availability and user entitlement (holding a certification course/bundle).
  const canShowTextbookStep = useMemo(() => canUserAccessTextbook(courses, activeCourse), [courses, activeCourse]);

  /**
   * Where the textbook step points. On desktop that is the sidebar's Textbook item; below the
   * sidebar/hamburger breakpoint that item lives inside the collapsed menu (so highlighting it
   * would frame nothing), and the on-page "Go to textbooks" button is the visible equivalent.
   */
  const textbookAnchor = useMemo(
    () =>
      window.innerWidth > widthBreakpoints.menu
        ? { element: '.textbook-nav', position: 'right' }
        : { element: '.textbooks', position: 'bottom' },
    [],
  );

  /**
   * Debug replay (`/home?replayOnboarding=1`, optionally `&onboardingAsTrial=1`), so the
   * walkthrough can be re-run from an account that has already burned its first login.
   * Read through `isDebugMode`, so the param is inert in production rather than merely hidden.
   * Pure lazy initialisers: React 18 StrictMode double-invokes them, so no side effects here.
   */
  const [isReplayingOnboarding, setIsReplayingOnboarding] = useState(
    () => isDebugMode && query.get(queryConstants.replayOnboarding) === '1',
  );
  const [replayAsTrial] = useState(() => isDebugMode && query.get(queryConstants.onboardingAsTrial) === '1');

  const isTrialOnboarding = isTrial || isReplayingOnboarding || replayAsTrial;

  useEffect(() => {
    const accent = getCoursePrimaryColor(activeCourse, colors.primary[1]);
    document.documentElement.style.setProperty('--onboarding-accent', accent);

    return () => {
      document.documentElement.style.removeProperty('--onboarding-accent');
    };
  }, [activeCourse, colors.primary]);

  /**
   * One definition array, so `stepCount` and the "N of M" denominator are both derived from it.
   * The trial upsell is now excluded by identity rather than by a `slice(0, -1)` on position,
   * which would silently mis-number every step once another one is inserted ahead of it.
   */
  const steps = useMemo(() => {
    const defs: OnboardingStepDef[] = [
      { key: 'welcome', title: '', description: '', position: 'right', isWide: true, showVideo: true },
      ...(canShowTextbookStep
        ? [{ key: 'textbook', title: '', description: '', ...textbookAnchor, isTextbook: true }]
        : []),
      {
        key: 'studyPlan',
        element: '.studyTime',
        position: 'right',
        title: 'Study Plan',
        description: 'You can tailor your personal study plan for better results',
      },
      {
        key: 'examDate',
        element: '.calendar',
        position: 'right',
        title: 'Exam Date Tracker ',
        description: 'When is your exam date? Choose the time!',
      },
      ...(isTrialOnboarding
        ? [
            {
              key: 'upsell',
              title: 'Hope you will have great success!',
              description: 'You are currently using a free trial version.',
              showUpgradeBullets: true,
            },
          ]
        : []),
    ];

    const totalSteps = defs.length;

    return defs.map((def, index) => {
      const getTooltipClass = () => {
        if (def.isTextbook) return isDarkMode ? 'tooltip-textbook-dark' : 'tooltip-textbook';
        if (def.isWide) return isDarkMode ? 'tooltip-one-step-dark' : 'tooltip-one-step';
        return isDarkMode ? 'tooltip-dark' : 'tooltip';
      };

      return {
        ...(def.element ? { element: def.element } : {}),
        ...(def.position ? { position: def.position } : {}),
        tooltipClass: getTooltipClass(),
        intro: def.isTextbook ? (
          // `theme` is passed explicitly: this renders through renderToStaticMarkup, outside every provider.
          <TextbookStepInfo
            theme={theme}
            isDarkMode={isDarkMode}
            stepCount={index + 1}
            totalSteps={totalSteps}
            readerHref={routes.reader}
          />
        ) : (
          <StepInfo
            title={def.title}
            description={def.description}
            stepCount={index + 1}
            totalSteps={totalSteps}
            showVideo={def.showVideo}
            showUpgradeBullets={def.showUpgradeBullets}
          />
        ),
      };
    });
  }, [canShowTextbookStep, textbookAnchor, isTrialOnboarding, isDarkMode, theme]);

  const onboardingOptions = useMemo(
    () => ({ showBullets: false, doneLabel: isTrialOnboarding ? 'Upgrade' : 'Done' }),
    [isTrialOnboarding],
  );

  const isOnboardingStart =
    (isReplayingOnboarding || (Boolean(isFirstLogin) && hasSeenOnboarding === 'true')) && stepsEnabled;

  const [getCourseByIdAction] = useAsyncAction(actions.courses.getCourseById);
  const [getCustomerSettings] = useAsyncAction(actions.settings.getCustomerSettings);

  const redirectStatus = query.get('redirect_status');
  const courseId = query.get(StorageKeys.COURSE_ID);
  const landingCourseId = query.get('landingCourseId');

  const subscribeType = query.get(StorageKeys.SUBSCRIBE_TYPE) as SubscribeDtoTypeEnum;
  const term = query.get(StorageKeys.TERM_PERIOD) as TermEnum;

  const isShowPaymentModal = courseId && subscribeType && term;
  const isStripeStatusSucceeded = redirectStatus === 'succeeded';

  /**
   * The single exit path for the walkthrough. A debug replay must never write the real
   * `hasSeenOnboarding` flag, or replaying once would consume a genuine first-login tour.
   */
  const finishOnboarding = () => {
    if (isReplayingOnboarding) {
      setIsReplayingOnboarding(false);
    } else {
      localStorage.setItem('hasSeenOnboarding', 'false');
    }
    setStepsEnabled(false);
  };

  const onExitOnboarding = (stepIndex?: number) => {
    if (stepIndex === -1) return;
    finishOnboarding();
  };

  const onCompleteOnboarding = () => {
    if (isTrialOnboarding) {
      void openChooseProductModalOpen();
      trackAmplitudeEvent('Upgrade Pressed');
    }
    finishOnboarding();
  };

  const selectCourseOptionsForPayment = (options: CourseOptionsForPayment) =>
    dispatch(actions.courses.selectCourseOptionsForPayment(options));

  const openPaymentModalOpen = () => {
    getEventSubscriptionPaymentModalOpenedGA();
    dispatch(actions.courses.openPaymentModalOpen());
  };

  const openChooseProductModalOpen = async () => {
    if (activeCourse) {
      await handleUpgradeBundle(activeCourse);
    } else {
      // Check if user has bundle access
      const userBundles = courses?.filter((course) => (course.type as string) === 'bundle') || [];

      // Check if user has trial bundles that can be upgraded
      const trialBundles = userBundles.filter((bundle) => {
        if (!bundle.currentPayment) return true;
        const { type } = bundle.currentPayment;
        return type === PaymentDtoTypeEnum.Free;
      });

      if (trialBundles.length > 0) {
        const nonBundleCourses = courses?.filter((course) => (course.type as string) !== 'bundle') || [];
        const relevantBundle = trialBundles[0];

        if (relevantBundle && nonBundleCourses.length > 0) {
          dispatch(
            actions.courses.openBundleUpgradeOptionsModal({
              bundle: relevantBundle,
              bundleCourses: nonBundleCourses,
            }),
          );
          localStorage.setItem('hasSeenOnboarding', 'false');
          return;
        }
      }

      dispatch(actions.courses.openChooseProductModalOpen());
    }
    localStorage.setItem('hasSeenOnboarding', 'false');
  };

  const handleCloseStreakModal = () => {
    setIsShowStreakModal(false);
  };

  // Bundle upgrade/upsell modal is mounted globally in CourseProvider

  const handleOpenStreakModal = () => {
    setIsShowStreakModal(true);
  };

  const handleOpenProfilePage = () => {
    navigate(routes.profile);
  };

  const handleShowInviteModalResolver = () => {
    setIsShowInviteModal(true);
  };

  const getCourseById = async (courseId: string) => {
    try {
      const course = await getCourseByIdAction(courseId);

      const prices = {
        [TermEnum.MONTHLY]: {
          [SubscribeDtoTypeEnum.Bronze]: course.prices.monthlySubscriptions?.bronze,
          [SubscribeDtoTypeEnum.Silver]: course.prices.monthlySubscriptions?.silver,
          [SubscribeDtoTypeEnum.Gold]: course.prices.monthlySubscriptions?.gold,
        },
        [TermEnum.YEARLY]: {
          [SubscribeDtoTypeEnum.Bronze]: course.prices.annualSubscriptions?.bronze,
          [SubscribeDtoTypeEnum.Silver]: course.prices.annualSubscriptions?.silver,
          [SubscribeDtoTypeEnum.Gold]: course.prices.annualSubscriptions?.gold,
        },
        [TermEnum.LIFE_TIME]: {
          [SubscribeDtoTypeEnum.Bronze]: course.prices.purchase,
          [SubscribeDtoTypeEnum.Silver]: course.prices.purchase,
          [SubscribeDtoTypeEnum.Gold]: course.prices.purchase,
        },
        [TermEnum.INSTALLMENT]: {
          [SubscribeDtoTypeEnum.Bronze]: course.prices.installment,
          [SubscribeDtoTypeEnum.Silver]: course.prices.installment,
          [SubscribeDtoTypeEnum.Gold]: course.prices.installment,
        },
      };

      const { amount = 0, oldAmount } = prices[term][subscribeType as SubscribeType] || {};
      const hasSubscription =
        course.currentPayment.type === 'Subscription' && course.currentPayment.status === 'active';

      selectCourseOptionsForPayment({
        courseId,
        subscribeType,
        term,
        type: course.type,
        amount,
        oldAmount,
        isUpdate: hasSubscription,
      });
      openPaymentModalOpen();
    } catch {
      errorToast('Something went wrong');
    }
  };

  const scrollToPlan = () => {
    planRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCloseInviteModal = () => {
    dispatch(actions.courses.inviteCourse(null));
    setIsShowInviteModal(false);
  };

  const fetchData = async () => {
    if (isLandingPage && landingCourseId && activeCourse && activeCourse?.id !== landingCourseId) {
      try {
        const course = await getCourseByIdAction(landingCourseId);
        console.log('🚀 ~ fetchData ~ course:', course);
        openChooseProductModalOpen();
      } catch {
        errorToast('Something went wrong');
      }
    } else if (isLandingPage && landingCourseId && activeCourse && activeCourse?.id === landingCourseId) {
      openChooseProductModalOpen();
    }
  };

  useEffect(() => {
    const fetchVariant = async () => {
      try {
        const onboardingVariant = await getOnboardingVariant();
        // console.log('🚀 ~ fetchVariant ~ onboardingVariant:', onboardingVariant);
        setVariant(onboardingVariant);
      } catch (error) {
        console.error('Error fetching upgrade button variant:', error);
      }
    };

    fetchVariant();
  }, [getOnboardingVariant]);

  useEffect(() => {
    fetchData();
  }, [isLandingPage, activeCourse]);

  // Keep the current active course unchanged after bundle payment for a smoother UX.

  useEffect(() => {
    setIsShowInstructionModal(isStripeStatusSucceeded);
  }, [isStripeStatusSucceeded]);

  useEffect(() => {
    if (isShowPaymentModal) {
      getCourseById(courseId);
    }
  }, [isShowPaymentModal]);

  useEffect(() => {
    if (inviteCourse?.id && inviteCourse.id !== inviteCourseId) {
      dispatch(actions.settings.setInviteCourseId(inviteCourse.id));
      handleShowInviteModalResolver();
    }
  }, [inviteCourse?.id]);

  /**
   * The textbook step's "Open my textbook" link. Tooltip markup goes through
   * renderToStaticMarkup, which strips React handlers, so the click is caught at the document
   * instead. The anchor keeps a real href, so it still navigates (via a full load) if this
   * listener is ever absent.
   */
  useEffect(() => {
    if (!isOnboardingStart) return undefined;

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const trigger =
        target && typeof target.closest === 'function'
          ? target.closest('[data-onboarding-action="open-reader"]')
          : null;

      if (!trigger) return;

      event.preventDefault();
      trackAmplitudeEvent('Onboarding Open Textbook Pressed');
      finishOnboarding();
      navigate(routes.reader);
    };

    document.addEventListener('click', handleDocumentClick);

    return () => document.removeEventListener('click', handleDocumentClick);
  }, [isOnboardingStart, isReplayingOnboarding]);

  useEffect(() => {
    if (!isOnboardingStart) return undefined;

    const handleVideoClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const videoContainer = target?.closest('.onboarding-video');
      const video = videoContainer?.querySelector('video');
      if (!video || !videoContainer?.closest('.tooltip-one-step, .tooltip-one-step-dark')) return;
      if (video.controls) return;

      video.currentTime = 0;
      video.muted = false;
      video.controls = true;
      videoContainer.classList.add('has-sound');
      void video.play().catch(() => undefined);
    };

    document.addEventListener('click', handleVideoClick);
    return () => document.removeEventListener('click', handleVideoClick);
  }, [isOnboardingStart]);

  useEffect(() => {
    getCustomerSettings();
  }, []);

  if (!activeCourse) {
    return <StyledMainLayout />;
  }

  const { hasPerformance } = activeCourse.currentPayment.level
    ? accessLevels[activeCourse.currentPayment.level]
    : accessLevels.null;

  return (
    <StyledMainLayout>
      <MainContainer>
        <HeaderWrapper>
          <TitleWrapper>
            <Title>
              {isFirstLogin ? 'Hello ' : 'Welcome back, '}
              <Transfer>{'\n'}</Transfer>
              {fullName && `${fullName}`}
            </Title>
            <SuggestionButton />
          </TitleWrapper>

          <RightSideWrapper>
            <Additionals className="selec">
              {isTrial && (
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => {
                    openChooseProductModalOpen();
                    trackAmplitudeEvent('Upgrade Pressed');
                  }}>
                  Upgrade
                </Button>
              )}
              {isGoldPaymentOrTrial && (
                <StreakFireButton
                  onClick={() => {
                    handleOpenStreakModal();
                    trackAmplitudeEvent('Streak Pressed');
                  }}
                />
              )}
              <StudyThisButton />
            </Additionals>
            <div className="calendar">
              <CalendarButton />
            </div>
            <Steps
              enabled={isOnboardingStart as boolean}
              steps={steps}
              onExit={(stepIndex) => onExitOnboarding(stepIndex)}
              initialStep={0}
              onComplete={onCompleteOnboarding}
              options={onboardingOptions}
            />
            <StyledIconButton
              iconComponent={<UserIcon />}
              onClick={() => {
                handleOpenProfilePage();
                trackAmplitudeEvent('Profile Pressed');
              }}
            />
          </RightSideWrapper>
        </HeaderWrapper>
        {/* Textbook access is visible only if the user has a certification course/bundle on their account.
            Same brand/host & certification course gate as the Sidebar's Textbook item. */}
        {canShowTextbookStep && (
          <TextbooksButton
            className="textbooks"
            onClick={() => {
              navigate(routes.reader);
              trackAmplitudeEvent('Textbook Pressed');
            }}>
            <MobileTextbookIcon aria-hidden="true">
              <path d="M231.65,194.55,198.46,36.75a16,16,0,0,0-19-12.39L132.65,34.42a16.08,16.08,0,0,0-12.3,19l33.19,157.8A16,16,0,0,0,169.16,224a16.25,16.25,0,0,0,3.38-.36l46.81-10.06A16.09,16.09,0,0,0,231.65,194.55ZM136,50.15c0-.06,0-.09,0-.09l46.8-10,3.33,15.87L139.33,66Zm6.62,31.47,46.82-10.05,3.34,15.9L146,97.53Zm6.64,31.57,46.82-10.06,13.3,63.24-46.82,10.06ZM216,197.94l-46.8,10-3.33-15.87L212.67,182,216,197.85C216,197.91,216,197.94,216,197.94ZM104,32H56A16,16,0,0,0,40,48V208a16,16,0,0,0,16,16h48a16,16,0,0,0,16-16V48A16,16,0,0,0,104,32ZM56,48h48V64H56Zm0,32h48v96H56Zm48,128H56V192h48v16Z" />
            </MobileTextbookIcon>
            <Text>Go to textbooks</Text>
            <CaretIcon color={colors.neutrals[1]} size={24} />
          </TextbooksButton>
        )}
        <Container>
          <div>
            <StudyPlan
              ref={planRef}
              setStepsEnabled={setStepsEnabled}
              openInstructionModal={() => setIsShowInstructionModal(true)}
              stepsEnabled={stepsEnabled}
            />
          </div>

          <Cover $hasPerformance={hasPerformance}>
            {hasPerformance && (
              <BonusWrapper>
                <StyledCheatSheet />
              </BonusWrapper>
            )}

            {hasPerformance ? (
              <>
                <Wrapper>
                  <Progress />
                  <Performance />
                </Wrapper>
              </>
            ) : (
              <>
                <BonusWithoutPerformanceWrapper>
                  <StyledCheatSheet />
                </BonusWithoutPerformanceWrapper>
                <PerformancePlug onClick={openChooseProductModalOpen} />
              </>
            )}
          </Cover>
          <ButtonContainer onClick={scrollToPlan}>
            <StyledCaret color={colors.primary[1]} size={24} />
            <Text>Go to plan</Text>
          </ButtonContainer>
        </Container>
      </MainContainer>
      <InstructionManualModal isOpen={isInstructionShowModal} onClose={() => setIsShowInstructionModal(false)} />
      <UpgradeSubscriptionModal
        isOpen={isShowUpgradeModal}
        onClose={() => setIsShowUpgradeModal(false)}
        manualType={ManualTypeEnum.PerformanceUpgrade}
      />

      {/* BundleUpgradeOptionsModal is mounted globally in CourseProvider */}

      <SuccessModal
        isOpen={isShowInviteModal}
        onClose={handleCloseInviteModal}
        title="Congratulations"
        text={`You have been invited to the course ${inviteCourse?.name}`}
      />

      {/** This condition is necessary to prevent early rendering of the component */}
      {isShowStreakModal && hasNoStreak ? (
        <NoStreakModal isModalVisible={isShowStreakModal} setModalVisible={handleCloseStreakModal} />
      ) : (
        <StreakModal isOpen={isShowStreakModal} onClose={handleCloseStreakModal} />
      )}

      <InstructionButtonContainer>
        <StyledButton
          variant="primary"
          onClick={() => {
            setIsShowInstructionModal(true);
          }}>
          Instruction manual
        </StyledButton>
      </InstructionButtonContainer>
    </StyledMainLayout>
  );
};

export default Home;

const StyledMainLayout = styled(MainLayout)`
  max-width: 1264px;
  padding-top: 40px;
  padding-right: 40px;
  padding-bottom: 40px;
  position: relative;

  ${respondToWidth.lg`
    padding-right: 32px;
  `}

  ${respondToWidth.sm`
    padding:0;
  `}
`;

export const MainContainer = styled.div`
  width: 100%;

  ${respondToWidth.sm`
   /* Bottom clearance for the fixed 84px Instruction Manual bar — without it the last card
      sits underneath the bar and reads as cut off. */
   padding: 20px 16px 100px 16px;
   margin-top: 20px;
  `}
`;

export const Title = styled(StyledText)`
  ${({ theme: { typography } }) => css`
    ${typography.title_2_bold_32}

    ${respondToWidth.sm`
      ${typography.title_4_bold_24}
    `}
  `};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;

  ${respondToWidth.sm`
    flex-direction: column;
  `};
`;

const Transfer = styled.span`
  margin: 0;
  padding: 0;
  display: none;

  ${respondToWidth.sm`
    display: block
  `};
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  margin-top: 30px;
  gap: 24px;
  padding-bottom: 40px;

  ${respondToWidth.ls`
    position: relative;
    flex-direction:column;
    width: 100%;
  `}

  ${respondToWidth.sm`
    position: relative;
    flex-direction:column-reverse;
    gap:0;
    padding-bottom: 0;
  `}
`;

/**
 * Intrinsically sized: the banner row follows the image's aspect ratio and the cards row grows
 * with its content. The previous fixed 160/456/84px rows (and a 748px min-height) forced the
 * banner to overflow its track, which a 13-step margin-top ladder on Wrapper then chased —
 * breaking at every width between two magic numbers on real phones.
 */
const Cover = styled.div<{ $hasPerformance: boolean }>`
  width: 100%;
  min-width: 0;
  flex-grow: 1;
  display: ${({ $hasPerformance }) => ($hasPerformance ? 'grid' : 'block')};
  /* minmax(0,…): with only rows declared, the implicit column track floors at the widest
     item's min-content (measured 376px on a 320px phone) and every item stretches to it. */
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto 1fr;
  align-content: start;
  gap: 24px;

  ${respondToWidth.sm`
    gap: 16px;
  `}
`;

/* The bonuses banner sizes itself (and carries its own artwork on study materials), so these only
   hold the spacing around it. */
const BonusWithoutPerformanceWrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;

  ${respondToWidth.sm`
    margin-bottom: 16px;
  `}
`;

const BonusWrapper = styled.div`
  width: 100%;
`;

const Wrapper = styled.div`
  width: 100%;
  min-width: 0;
  display: grid;
  /* minmax(0,…) everywhere: bare 1fr floors at min-content and overflows narrow phones. */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;

  ${respondToWidth.lg`
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto;
  `}

  ${respondToWidth.sm`
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto;
    gap: 16px;
  `}
`;

/** The "Go to plan" look, but visible on every viewport — textbooks are a headline entry. */
const TextbooksButton = styled.button`
  width: 100%;
  border-radius: 12px;
  padding-block: 10px;
  margin-top: 16px;
  /* Mobile-only (like "Go to plan"): desktop's sidebar already has the Textbook item. */
  display: none;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
  background-color: ${({ theme: { colors } }) => colors.neutrals[11]};
  border: none;
  outline: none;
  cursor: pointer;
  margin-bottom: 16px;

  ${respondToWidth.menu`
    display: flex;
  `}
`;

const MobileTextbookIcon = styled.svg.attrs({
  viewBox: '0 0 256 256',
  fill: 'currentColor',
})`
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
`;

const ButtonContainer = styled.button`
  width: 100%;
  border-radius: 12px;
  padding-block: 6px;
  display: none;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme: { colors } }) => colors.neutrals[11]};
  border: none;
  outline: none;
  cursor: pointer;

  ${respondToWidth.sm`
    display: flex;
    margin-bottom: 16px;
  `}
`;

const Text = styled(StyledText)`
  margin-left: 6px;
  ${({ theme: { typography } }) => typography.body_basic_bold_14}
`;

const StyledCaret = styled(CaretIcon)`
  transform: rotate(90deg);
`;

const InstructionButtonContainer = styled.div`
  display: none;
  width: 100%;
  height: 84px;
  padding: 16px;
  border-top: 1px solid ${({ theme: { colors } }) => colors.neutrals[9]};
  background-color: ${({ theme: { colors } }) => colors.neutrals[11]};
  border-radius: 0;
  position: fixed;
  z-index: 2;
  bottom: 0;
  ${respondToWidth.sm`
    display: block;
  `}
`;

const StyledButton = styled(Button)`
  padding-inline: 0;
  text-transform: capitalize;
`;

const StyledCheatSheet = styled(Bonuses)`
  ${respondToWidth.sm`
  margin-bottom: 16px;
  `}
`;

const RightSideWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  ${respondToWidth.ls`
    position: relative;
    flex-direction: column-reverse;
  `}

  ${respondToWidth.sm`
    flex-direction: row;
    /* Calendar + the trial Upgrade button exceed narrow phones side by side: the calendar is
       width:auto over white-space:nowrap text (~270px) so it cannot shrink, and the button has
       no room left. Wrapping drops Upgrade onto its own line instead of clipping it off-screen.
       A stray nowrap declaration used to follow this and silently defeated it. */
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: flex-start;
    /* The calendar's date popover escapes this box. */
    overflow: visible;
    width: 100%;

    .calendar {
      flex: 0 0 auto;
      width: auto;
      order: 1;
      align-self: flex-end;
      margin-top: 6px;
    }

    .selec {
      order: 2;
      align-self: flex-end;
      margin-top: 6px;
    }
  `}
`;

const Additionals = styled.div`
  align-self: flex-end;
  display: flex;
  align-items: center;
  gap: 8px;

  ${respondToWidth.sm`
    align-self: center;
    flex: 0 0 auto;
  `}
`;

const StyledIconButton = styled(IconButton)`
  max-height: 36px;

  ${respondToWidth.menu`
    display: none;
  `}
`;
